import { Injectable } from '@angular/core';
import { AnnexesListWithDetailsOriginalDto, AnnexListWithDetailsDto } from 'src/app/models/dtos/annexes/annex-list-with-details-dto';
import { PropertyHistoryEntry, PropertyType } from '../../shared/components/property-with-history/models/property-history-entry';
import { WageHistoryEntry } from 'src/app/shared/components/wages-with-history/models/wages-with-history-entry';

@Injectable({ providedIn: 'root' })
export class AgreementHistoryService {
  buildPropertyHistory(
    propertyType: PropertyType,
    originalValues: AnnexesListWithDetailsOriginalDto,
    annexes: AnnexListWithDetailsDto[],
  ): PropertyHistoryEntry[] {
    const config = this.getConfig(propertyType);

    const relevantAnnexes = annexes.filter((a) => a[config.changesFlag]);

    if (relevantAnnexes.length === 0) {
      return [];
    }

    const currentFlag = config.currentFlag;

    const history: PropertyHistoryEntry[] = relevantAnnexes.map((annex, index) => ({
      annexId: annex.AnnexId,
      conclusionDate: annex.ConclusionDate,
      startDate: annex.StartDate,
      value: annex[config.valueName] ?? '-',
      isOriginal: false,
      isCurrent: annex[currentFlag] ?? false
    }));

    // WorkerAgreement
    history.push({
      annexId: null,
      conclusionDate: originalValues.ConclusionDate,
      startDate: originalValues.EmploymentDateFrom,
      value: originalValues[config.originalValueName] ?? '-',
      isOriginal: true,
      isCurrent: !history.some(h => h.isCurrent),
    });

    return history;
  }

  buildWagesHistory(originalValues: AnnexesListWithDetailsOriginalDto, annexes: AnnexListWithDetailsDto[]): WageHistoryEntry[] {
    const relevantAnnexes = annexes.filter((a) => a.ChangesWage);

    if (relevantAnnexes.length === 0) {
      return [];
    }

    const history: WageHistoryEntry[] = relevantAnnexes.map((annex, index) => ({
      annexId: annex.AnnexId,
      conclusionDate: annex.ConclusionDate,
      startDate: annex.StartDate,
      value: '',
      wages: annex.Wages,
      isOriginal: false,
      isCurrent: annex.IsCurrentForWage ?? false,
    }));

    // WorkerAgreement
    history.push({
      annexId: null,
      conclusionDate: originalValues.ConclusionDate,
      startDate: originalValues.EmploymentDateFrom,
      value: '',
      wages: originalValues.Wages,
      isOriginal: true,
      isCurrent: !history.some(h => h.isCurrent),
    });

    return history;
  }

  private getConfig(propertyType: PropertyType) {
    const configs: Record<PropertyType, { changesFlag: string; currentFlag: string; valueName: string; originalValueName: string }> = {
      jobTitle: { changesFlag: 'ChangesJobTitle', currentFlag: 'IsCurrentForJobTitle', valueName: 'JobTitleName', originalValueName: 'JobTitleName' },
      organizationalUnit: {
        changesFlag: 'ChangesJobTitle',
        currentFlag: 'IsCurrentForJobTitle',
        valueName: 'OrganizationalUnitName',
        originalValueName: 'OrganizationalUnitName',
      },
      workplace: {
        changesFlag: 'ChangesTemporaryWorkplace',
        currentFlag: 'IsCurrentForTemporaryWorkplace',
        valueName: 'TemporaryWorkplaceName',
        originalValueName: 'TemporaryWorkplaceName',
      },
      supervisor: {
        changesFlag: 'ChangesSupervisor',
        currentFlag: 'IsCurrentForSupervisor',
        valueName: 'PotentialSupervisorName',
        originalValueName: 'PotentialSupervisorName',
      },
      wages: { changesFlag: 'ChangesWage', currentFlag: 'IsCurrentForWage', valueName: 'Wages', originalValueName: 'Wages' },
      workingTimeGroup: {
        changesFlag: 'ChangesWorkingTimeGroup',
        currentFlag: 'IsCurrentForWorkingTimeGroup',
        valueName: 'WorkingTimeGroupName',
        originalValueName: 'WorkingTimeGroupName',
      },
    };

    return configs[propertyType];
  }
}
